import {Network} from "../../../network/Network";
import {ProductType} from "../../../models/product/ProductType";
import {HttpStatus} from "../../../network/status/HttpStatus";
import {INetworkComponent} from "../../../network/types/INetworkComponent";
import {ICommunitySEOBacklinksCampaignIncoming} from "../../../models/community/seo/backlink/campaign/ICommunitySEOBacklinksCampaignIncoming";
import {ICommunitySEOBacklinksCampaignAdminIncoming} from "../../../models/community/seo/backlink/campaign/ICommunitySEOBacklinksCampaignAdminIncoming";
import {IBacklinkArticleAdminIncoming} from "../../../models/community/seo/backlink/campaign/IBacklinkArticleAdminIncoming";
import {ICommunitySEOBacklinksCampaignOverviewIncoming} from "../../../models/community/seo/backlink/campaign/ICommunitySEOBacklinksCampaignOverviewIncoming";
import {ICommunitySEOBacklinksCampaignPagesIncoming} from "../../../models/community/seo/backlink/campaign/ICommunitySEOBacklinksCampaignPagesIncoming";
import {ICommunitySEOBacklinksCampaignSelectPagesOutgoing} from "../../../models/community/seo/backlink/campaign/ICommunitySEOBacklinksCampaignSelectPagesOutgoing";
import {ICommunitySEOBacklinksCampaignSelectKeywordsOutgoing} from "../../../models/community/seo/backlink/campaign/ICommunitySEOBacklinksCampaignSelectKeywordsOutgoing";
import {ICommunitySEOBacklinksCampaignSelectBacklinksOutgoing} from "../../../models/community/seo/backlink/campaign/ICommunitySEOBacklinksCampaignSelectBacklinksOutgoing";
import {ICommunitySEONewPagesSearchOutgoing} from "../../../models/community/seo/newPageTracking/ICommunitySEONewPagesSearchOutgoing";

import {ICommunitySEOBacklinksCampaignAutopilotModel} from "../../../models/community/seo/backlink/campaign/autopilot/ICommunitySEOBacklinksCampaignAutopilotModel";
import {Services} from "../../Services";
import {ErrorCode} from "../../../network/status/error/ErrorCode";
import {Resources} from "../../../resources/Resources";
import {arrayRemove} from "../../../sedestral/framework/utilities/ArrayRemove";
import {WebSocketCommunityEventName} from "../../../network/socket/names/WebSocketCommunityEventName";

export class CommunityBacklinkCampaignService {

    public static campaignEvents: ((campaign: ICommunitySEOBacklinksCampaignIncoming) => void)[] = [];

    public static init(): void {
        Network.on(ProductType.COMMUNITY, WebSocketCommunityEventName.COMMUNITY_SEO_BACKLINK_CAMPAIGN_UPDATE, (campaign: ICommunitySEOBacklinksCampaignIncoming) => {
            this.campaignEvents.forEach(callback => callback(campaign));
        });
    }

    public static dispose(): void {
        this.campaignEvents = [];
    }

    public static onCampaignUpdate(component: INetworkComponent, func: (campaign: ICommunitySEOBacklinksCampaignIncoming) => void): void {
        this.campaignEvents.push(func);
        component.onRemove(() => arrayRemove(this.campaignEvents, func));
    }

    public static async getCampaignPages(campaignId: string, data: ICommunitySEONewPagesSearchOutgoing, component?: INetworkComponent): Promise<ICommunitySEOBacklinksCampaignPagesIncoming> {
        let request = await Network.postJson(ProductType.COMMUNITY, `/community/backlinks/campaigns/${campaignId}/pages`, data, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async create(communityId: string, pageUrl?: string, component?: INetworkComponent): Promise<ICommunitySEOBacklinksCampaignIncoming> {
        const body = pageUrl ? {pageUrl} : {};
        let request = await Network.postJson(ProductType.COMMUNITY, `/community/backlinks/campaigns/${communityId}`, body, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async selectPages(campaignId: string, data: ICommunitySEOBacklinksCampaignSelectPagesOutgoing, component?: INetworkComponent): Promise<ICommunitySEOBacklinksCampaignIncoming> {
        Services.handleErrors(component, [
            {errorCode: ErrorCode.CAMPAIGN_PAGES_EMPTY, message: Resources.t("words.campaignPagesEmpty")},
            {errorCode: ErrorCode.CAMPAIGN_INVALID_STATE, message: Resources.t("words.campaignInvalidState")}
        ]);

        let request = await Network.postJson(ProductType.COMMUNITY, `/community/backlinks/campaigns/${campaignId}/select-pages`, data, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async selectKeywords(campaignId: string, data: ICommunitySEOBacklinksCampaignSelectKeywordsOutgoing, component?: INetworkComponent): Promise<ICommunitySEOBacklinksCampaignIncoming> {
        Services.handleErrors(component, [
            {errorCode: ErrorCode.CAMPAIGN_PAGES_EMPTY, message: Resources.t("words.campaignPagesEmpty")}
        ]);

        let request = await Network.postJson(ProductType.COMMUNITY, `/community/backlinks/campaigns/${campaignId}/select-keywords`, data, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async selectBacklinks(campaignId: string, data: ICommunitySEOBacklinksCampaignSelectBacklinksOutgoing, component?: INetworkComponent): Promise<ICommunitySEOBacklinksCampaignIncoming> {
        Services.handleErrors(component, [
            {errorCode: ErrorCode.CAMPAIGN_PAGES_EMPTY, message: Resources.t("words.campaignPagesEmpty")}
        ]);

        let request = await Network.postJson(ProductType.COMMUNITY, `/community/backlinks/campaigns/${campaignId}/select-backlinks`, data, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async payment(campaignId: string, component?: INetworkComponent): Promise<ICommunitySEOBacklinksCampaignIncoming> {
        Services.handleErrors(component, [
            {errorCode: ErrorCode.INSUFFICIENT_CREDIT, message: Resources.t("words.insufficientCredit")}
        ]);

        let request = await Network.postJson(ProductType.COMMUNITY, `/community/backlinks/campaigns/${campaignId}/payment`, {}, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async reopenBacklinks(campaignId: string, component?: INetworkComponent): Promise<ICommunitySEOBacklinksCampaignIncoming> {
        let request = await Network.postJson(ProductType.COMMUNITY, `/community/backlinks/campaigns/${campaignId}/reopen-backlinks`, {}, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async retryBacklinks(campaignId: string, component?: INetworkComponent): Promise<ICommunitySEOBacklinksCampaignIncoming> {
        let request = await Network.postJson(ProductType.COMMUNITY, `/community/backlinks/campaigns/${campaignId}/retry-backlinks`, {}, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async delete(campaignId: string, component?: INetworkComponent): Promise<boolean> {
        let request = await Network.delete(ProductType.COMMUNITY, `/community/backlinks/campaigns/${campaignId}`, component);
        return request.status == HttpStatus.OK;
    }

    public static async findById(campaignId: string, component?: INetworkComponent): Promise<ICommunitySEOBacklinksCampaignIncoming> {
        let request = await Network.get(ProductType.COMMUNITY, `/community/backlinks/campaigns/${campaignId}`, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async findAllByCommunityId(communityId: string, component?: INetworkComponent): Promise<ICommunitySEOBacklinksCampaignOverviewIncoming[]> {
        let request = await Network.get(ProductType.COMMUNITY, `/community/backlinks/campaigns/community/${communityId}`, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async getAutopilot(communityId: string, component?: INetworkComponent): Promise<ICommunitySEOBacklinksCampaignAutopilotModel> {
        let request = await Network.get(ProductType.COMMUNITY, `/community/backlinks/autopilot/${communityId}`, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async updateAutopilot(communityId: string, data: ICommunitySEOBacklinksCampaignAutopilotModel, component?: INetworkComponent): Promise<ICommunitySEOBacklinksCampaignAutopilotModel> {
        let request = await Network.postJson(ProductType.COMMUNITY, `/community/backlinks/autopilot/update/${communityId}`, data, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async getAdminCampaigns(communityId?: string, component?: INetworkComponent): Promise<ICommunitySEOBacklinksCampaignAdminIncoming[]> {
        const url = communityId
            ? `/community/admin/backlinks/campaigns?communityId=${communityId}`
            : `/community/admin/backlinks/campaigns`;
        let request = await Network.get(ProductType.COMMUNITY, url, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async adminRetryOrder(campaignId: string, remoteId: string, component?: INetworkComponent): Promise<ICommunitySEOBacklinksCampaignIncoming> {
        let request = await Network.postJson(ProductType.COMMUNITY, `/community/admin/backlinks/campaigns/${campaignId}/retry-order/${remoteId}`, {}, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async adminRefundBacklink(campaignId: string, remoteId: string, component?: INetworkComponent): Promise<ICommunitySEOBacklinksCampaignIncoming> {
        let request = await Network.postJson(ProductType.COMMUNITY, `/community/admin/backlinks/campaigns/${campaignId}/refund/${remoteId}`, {}, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async getAdminCampaignDetail(campaignId: string, component?: INetworkComponent): Promise<ICommunitySEOBacklinksCampaignIncoming> {
        let request = await Network.get(ProductType.COMMUNITY, `/community/admin/backlinks/campaigns/${campaignId}`, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async adminGetArticle(articleId: string, component?: INetworkComponent): Promise<IBacklinkArticleAdminIncoming> {
        let request = await Network.get(ProductType.COMMUNITY, `/community/admin/backlinks/article/${articleId}`, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async adminUpdateArticle(articleId: string, html: string, component?: INetworkComponent): Promise<IBacklinkArticleAdminIncoming> {
        let request = await Network.postJson(ProductType.COMMUNITY, `/community/admin/backlinks/article/${articleId}/update`, {html}, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async adminResubmitArticle(campaignId: string, remoteId: string, component?: INetworkComponent): Promise<ICommunitySEOBacklinksCampaignIncoming> {
        let request = await Network.postJson(ProductType.COMMUNITY, `/community/admin/backlinks/campaigns/${campaignId}/resubmit/${remoteId}`, {}, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }
}
